import type { LocaleMessageSchema } from '../types';

export default {
  Common: {
    loading: 'Carregando...',
  },
  errors: {
    session_create_error: 'Falha ao criar sessão.\nPor favor, tente novamente.',
    session_continue_error:
      'Não conseguimos carregar sua sessão. Por favor, tente novamente. Se o problema persistir, entre em contato com o suporte em {supportEmail}.',
    kiosk_not_found:
      'Falha ao carregar o quiosque.\nPor favor, tente novamente.',

    art_style_missing: 'Nenhum estilo de arte foi\nadicionado a este quiosque',
    art_style_fetch_error: 'Falha ao carregar o estilo de arte',
    art_style_preload_error:
      'Falha ao carregar pré-visualização do estilo de arte',

    face_detection_server_error:
      'Falha ao processar a foto. Por favor, tente novamente.',
    face_detection_preload_error: 'Erro ao pré-carregar faces detectadas',
    face_detection_no_faces:
      'Nenhuma face detectada. Por favor, tente tirar outra foto.',

    photo_webcam_not_allowed: 'Permissão da webcam negada',
    photo_webcam_not_found:
      'Webcam não encontrada. Verifique se está conectada ao quiosque.',
    photo_webcam_hardware_error:
      'A webcam está ocupada ou não está funcionando.\nTente novamente ou peça ajuda.',
    photo_webcam_photo_failed: 'Falha ao tirar a foto\ncom a webcam',

    photo_phone_missing_session_id:
      'Não conseguimos encontrar sua sessão.\nPor favor, escaneie o código QR novamente.',
    photo_phone_missing_kiosk_slug:
      'Quiosque não encontrado em sua sessão. Se o problema persistir, entre em contato com o suporte em {supportEmail}.',
    photo_phone_limit_exceeded: 'Muitas fotos selecionadas',
    photo_phone_qr_error: 'Falha ao gerar código QR\npara upload da foto',

    photo_file_unsupported_type: 'Tipo de arquivo não suportado',
    photo_file_cannot_process:
      'Não foi possível processar esta foto.\nPor favor, tente outra.',
    photo_file_cannot_process_heic:
      'Não foi possível processar a foto HEIC.\nPor favor, tente outra.',

    portrait_generation_error:
      'Ocorreu um erro ao gerar seu retrato. Por favor, tente novamente.',
    portrait_generation_faces_not_selected:
      'Nenhuma face selecionada para\ngerar retrato',
    portrait_generation_style_not_selected:
      'Nenhum estilo de arte selecionado para\ngerar retrato',
    portrait_generation_over_limit: 'Muitas tentativas de geração',

    portraits_download_session_error:
      'Não conseguimos encontrar sua sessão. Verifique o código que você digitou. Se o problema persistir, entre em contato com o suporte em {supportEmail}.',
    portraits_download_kiosk_error:
      'Quiosque não encontrado em sua sessão. Se o problema persistir, entre em contato com o suporte em {supportEmail}.',
    portraits_download_portraits_error:
      'Não foi encontrado nenhum retrato na sua sessão. Se o problema persistir, contacte o suporte através de {supportEmail}.',

    payment_partner_mode_missing_stripe_account:
      'O modo de pagamento do parceiro requer uma conta Stripe Connect. Configure a conta Stripe do parceiro ou mude para o modo de pagamento da plataforma.',

    api_error_missing_session_id: 'Sessão não encontrada',
    api_error_email_code_validation:
      'Falha ao validar o código de pagamento. Por favor, tente novamente.',
  },

  /**
   * Global components
   */

  OverlayError: {
    buttons: {
      close: 'Fechar',
      restart: 'Recomeçar',
      retry: 'Tentar novamente',
    },
  },
  Logo: {
    startOver: 'Recomeçar',
  },
  Copyright: {
    copyright: 'powered by TheArtisan.ai',
    distribution: 'distribuído por',
    terms:
      'Ao tirar uma foto neste dispositivo, você aceita nossas políticas e termos. Para revisá-los, por favor',
    termsLink: 'clique aqui',
  },
  IdlePopup: {
    title: 'Ainda está aqui?',
    caption: 'Sua sessão terminará se você estiver inativo.',
    buttonContinue: 'Sim, estou aqui',
    buttonRestart: 'Não, recomeçar',
  },
  GDPRConsent: {
    title: 'Consentimento necessário',
    consentText:
      'Precisamos do seu consentimento para processar suas características faciais (dados biométricos) para criar transformações artísticas. Você só pode continuar se tiver 16 anos ou mais (ou consentimento dos pais).',
    buttonConsent: 'Eu concordo',
    buttonDecline: 'Eu não concordo',
    privacyPolicyLink: 'Ver política de privacidade',
    declineMessage: 'Você não pode continuar sem consentimento',
  },
  StepProgressIndicator: {
    photoShort: 'Foto',
    photoLong: 'Tirar Foto',
    facesShort: 'Faces',
    facesLong: 'Escolher Faces',
    groupPortraitsShort: 'Grupo',
    groupPortraitsLong: 'Retratos de grupo',
    portraitsShort: 'Retratos',
    portraitsLong: 'Escolher Retratos',
    printShort: 'Imprimir',
    printLong: 'Pagar & Imprimir',
    sendToPickupShort: 'Retirada',
    sendToPickupLong: 'Enviar para retirada',
  },
  UploadedPhotosSidebar: {
    title: 'Suas Fotos',
    count: '+{extraPhotoCount} mais',
  },
  NumericKeyboard: {
    invalidPin: 'Código PIN inválido',
    clear: 'Limpar',
    enter: 'Entrar',
  },
  OutOfService: {
    message:
      'Seu contrato parece estar pausado ou expirado.\nPor favor, entre em contato com seu distribuidor para obter ajuda.',
  },

  /**
   * Steps
   */

  Home: {
    title: 'Gere seu retrato em segundos!',
    button: 'Tente Agora!',
  },
  ArtStyle: {
    title: 'Escolha seu Estilo',
  },
  PhotoFromWebcam: {
    titleGetReady: 'Prepare-se para tirar uma foto',
    titleAttention: 'Olhe para a câmera!',
    titleReview: 'Gostou desta foto?',
    titleUpload: 'Carregando sua foto…',
    buttonPrimaryPhoto: 'Tirar Foto',
    buttonPrimaryUse: 'Sim, Usar Esta Foto',
    buttonPrimaryRetake: 'Refazer Foto',
    buttonSecondaryUpload: 'Enviar do Telefone',
    buttonSecondaryRetake: 'Não, Refazer (1 Tentativa Restante)',
    buttonSecondaryUseFirst: 'Não, Usar a Primeira Foto',
    titleCameraError: 'Câmera indisponível',
    buttonRetryCamera: 'Tentar câmera novamente',
  },
  PhotoPromptFromPhone: {
    title: 'Enviar fotos do telefone?',
    caption:
      'Surpreenda amigos e família —\nenvie suas fotos para criar retratos.',
    buttonUpload: 'Sim, enviar do telefone',
    buttonSkip: 'Não, pular envio do telefone',
  },
  PhotoFromPhone: {
    title: 'Enviar fotos do seu telefone',
    step1: 'Escaneie o código QR com seu telefone',
    step2: 'Selecione fotos',
    step3: 'Aguarde o upload',
    waiting: 'Aguardando upload…',
    done: 'Upload realizado com sucesso',
    buttonCancel: 'Cancelar & Voltar',
    buttonSkip: 'Pular envio do telefone',
    buttonContinue: 'Continuar',
    gdprConsentAll:
      'Tenho consentimento de todas as pessoas nesta foto para processar suas características faciais',
    gdprOver16: 'Ninguém nesta foto tem menos de 16 anos',
    gdprResponsibility:
      'Aceito total responsabilidade pela conformidade com o RGPD',
    gdprAgreeToPrefix: 'Concordo com os',
    gdprPrivacyPolicyLink: 'termos da política de privacidade',
    gdprAgreeToSuffix: 'para fotos enviadas',
    gdprCheckboxesRequired:
      'Por favor, marque todas as caixas abaixo para continuar',
    buttonSkipKioskPhoto: 'Pular foto do quiosque',
    buttonTakeWebcamPhoto: 'Tirar foto com webcam',
  },
  FacesIndividual: {
    title: 'De quem você quer criar retratos individuais?',
    buttonConfirm: 'Confirmar & Continuar',
    genderMale: 'Masculino',
    genderFemale: 'Feminino',
    genderNonBinary: 'Não-binário',
    age0to2: '0-2',
    age3to9: '3-9',
    age10to19: '10-19',
    age20to29: '20-29',
    age30to39: '30-39',
    age40to49: '40-49',
    age50to59: '50-59',
    age60to69: '60-69',
    age70plus: '70+',
    raceWhite: 'Branco',
    raceBlack: 'Negro',
    raceEastAsian: 'Asiático oriental',
    raceSoutheastAsian: 'Asiático do sudeste',
    raceIndian: 'Indiano',
    raceLatino: 'Latino',
    raceMena: 'Médio Oriente',
  },
  Portraits: {
    title: 'Escolha seus retratos!',
    buttonNext: 'Próximo: Imprimir Selecionados',
    buttonNextGroupOnly: 'Continuar apenas com retratos de grupo',
    galleryScrollBack: 'Retratos anteriores',
    galleryScrollForward: 'Próximos retratos',
    filterLabel: 'Mostrar',
    filterAll: 'Todos',
    filterGroup: 'Juntos',
    filterIndividual: 'Individuais',
    stageKindGroup: 'Retrato de grupo',
    stageKindIndividual: 'Retrato',
    stagePosition: '{index} de {count}',
    stageSelect: 'Selecionar',
    stageSelected: 'Selecionado ✓',
    buttonMore: 'Fazer mais retratos ({count} restantes)',
    popupTitle: 'Deseja finalizar seu pedido?',
    popupGroupSelected: 'Retratos de grupo selecionados: {count} ({price})',
    popupIndividualSelected:
      'Retratos individuais selecionados: {count} ({price})',
    popupTotalSum: 'Valor total: {sum}',
    popupButtonNo: 'Não, voltar',
    popupButtonYes: 'Sim, imprimir & pagar!',
    error: 'Ops! Não foi possível gerar este retrato.',
    placeholderGenerating: 'Gerando seu retrato…',
    placeholderLoading: 'Carregando seu retrato…',
    groupPlaceholder: 'Seu retrato de grupo aparecerá aqui',
    buttonRetry: 'Tentar Novamente',
    buttonErrorDetails: 'Detalhes do Erro',
    retrying: 'Tentando novamente...',
    maxRetriesReached: 'Máx. tentativas atingidas',
    retryAttempts: 'Tentativas: {count}',
    printingLockedPopupTitle:
      'Por favor, chame o funcionário mais próximo para imprimir seus retratos.',
    generationPendingTitle: 'Alguns retratos ainda estão a ser criados',
    generationPendingCaption:
      'Ainda estão a ser criados {count} retratos. Quer esperar para ver todos?',
    generationPendingWait: 'Esperar por todos',
    generationPendingSendReady: 'Enviar {count} retratos prontos',
    printingLockedPopupCaption:
      'A impressão no quiosque está indisponível enquanto nenhum funcionário estiver presente.',
  },
  PortraitsGroup: {
    buttonMore: 'Fazer mais retratos',
  },
  PrintLock: {
    lockTitle: 'Bloquear impressão',
    unlockTitle: 'Desbloquear impressão',
  },
  PaymentType: {
    title: 'Como você gostaria de pagar?',
    optionCardTitle: 'Cartão de Crédito',
    optionCardCaption: 'Visa, Mastercard, & 10+ cartões suportados',
    optionVoucherTitle: 'Retratos Pré-pagos',
    optionVoucherCaption: 'Incluído no bilhete — peça ao funcionário',
  },
  PaymentTerminal: {
    title: 'Por favor, siga as instruções no terminal de pagamento',
    titlePaymentSuccess: 'Imprimindo seus retratos…',
    titlePaymentError: 'Pagamento falhou',
    titlePrintError1: 'Desculpe, alguns retratos não puderam ser impressos',
    titlePrintError2:
      'Por favor, pressione "Imprimir Novamente" abaixo para tentar novamente.',
    buttonRetryPrinting: 'Imprimir Novamente',
    buttonRetry: 'Tentar novamente',
    buttonCancel: 'Cancelar',
    buttonExitSession: 'Recomeçar',
    buttonBack: 'Voltar',
    total: 'Total:',
    captionPayment: 'Aguardando seu pagamento…',
    captionPrinting: 'Impressão realizada com sucesso',
    captionPaymentError:
      'Algo deu errado. Por favor, tente novamente ou recomece.',
    captionPaymentFinal:
      'O pagamento não funcionou. Peça ajuda a um funcionário.',
    failureCaptions: {
      notEnoughFunds: 'O cartão não tem saldo suficiente. Tente outro cartão.',
      expiredCard: 'Este cartão está expirado. Tente outro cartão.',
      cardDeclined: 'O cartão foi recusado. Tente outro cartão.',
      cardError: 'Não é possível usar este cartão aqui. Tente outro cartão.',
      processingError:
        'O pagamento não foi concluído. Tente novamente com o mesmo cartão.',
      readerError:
        'O leitor de cartões não está disponível. Peça ajuda a um funcionário.',
      timeout: 'O pagamento demorou muito. Tente novamente.',
    },
    errorMessage: 'Desculpe, não conseguimos processar seu pagamento',
    popupCancelledTitle: 'Pagamento cancelado',
    popupCancelledCaption: 'Gostaria de tentar novamente ou recomeçar?',
    popupButtonContinue: 'Tentar novamente',
  },
  PaymentVoucher: {
    title:
      'Por favor, entre em contato com um funcionário para pagar com um voucher',
    buttonBack: 'Voltar',
    pinTitle: 'Pagar com voucher',
  },
  Printing: {
    title: 'Imprimindo seus retratos…',
    titleError: 'Desculpe, alguns retratos não puderam ser impressos',
    buttonPrintAgain: 'Imprimir Novamente',
    buttonContinueWithoutPrint: 'Continuar sem imprimir',
    error: 'Ops! Não foi possível imprimir este retrato.',
    errorPartial: '{printed} de {total} impressos',
    retrying: 'Tentando novamente…',
    waiting: 'Aguardando a impressora…',
  },
  PrintFailed: {
    title: 'Lamentamos, não foi possível imprimir as suas fotos',
    titlePartial: '{printed} de {total} fotos impressas',
    caption: 'Pode iniciar uma nova sessão e criar novos retratos.',
    captionRefunded: 'Reembolsámos {amount} no seu cartão.',
    captionNotCharged: 'Não cobrámos {amount}.',
    captionRefundHelp: 'Um membro da equipa irá ajudá-lo com o reembolso.',
  },
  Success: {
    title: 'Obrigado!',
    caption: 'Seus retratos foram impressos. Por favor, retire-os na recepção.',
    buttonStartAgain: 'Começar Novamente',
  },
  GoPhotoScan: {
    title: 'Digitalize seu PhotoPass',
    subtitle: 'Apresente o código QR do seu PhotoPass ao scanner',
    invalidCode: 'Formato de código inválido. Por favor, digitalize novamente.',
    failedToRegister:
      'Falha ao registrar o código. Por favor, tente novamente.',
    error: 'Ocorreu um erro. Por favor, tente novamente.',
  },

  /**
   * Customer-facing components
   */

  CustomerPhotoFromPhone: {
    title: 'Enviar fotos da sua galeria',
    buttonSelect: 'Selecionar Fotos',
    maxCount: 'Máximo de {count} fotos',
    uploading: 'Enviando…',
    uploadError:
      'Falha no envio. Por favor, verifique sua conexão e tente novamente.',
    buttonRetry: 'Tentar Novamente',
    successTitle: 'Upload realizado com sucesso',
    successCaption:
      'Por favor, volte ao quiosque e siga as instruções na tela.',
  },
  CustomerPortraitsDownload: {
    title: 'Introduza o código para obter o seu retrato',
    buttonSubmit: 'Enviar',
    error:
      'Este código não parece correto. Verifique o código de 8 caracteres impresso na sua fotografia e tente novamente.',
    errors: {
      wrongFormat:
        'Este código não parece correto. Verifique o código de 8 caracteres impresso na sua fotografia e tente novamente.',
      notFound:
        'Lamentamos, não encontrámos nenhum retrato. Verifique o código — está impresso na frente do retrato, no canto inferior esquerdo.',
      serverError:
        'Lamentamos, não foi possível obter os seus retratos neste momento. Tente novamente mais tarde.',
    },
  },
  CustomerSessionPass: {
    tabs: {
      photos: 'Fotos',
      pass: 'Passe QR',
    },
    eyebrow: 'O seu passe de sessão',
    title: 'Mantenha esta página aberta',
    description:
      'No ponto de compra, digitalize o código quadrado abaixo. Os seus retratos estão disponíveis no separador Fotos.',
    descriptionNothingPrinted:
      'No ponto de venda, digitalize o código quadrado abaixo para escolher e imprimir as suas fotos.',
    scanLabel: 'Digitalizar no ponto de compra',
    codeLabel: 'Código da sessão',
    ready: 'Pronto para digitalizar',
    loading: 'A preparar o código',
    unavailable: 'Código indisponível',
    qrAlt: 'Código QR para o ponto de compra',
    defaultVenue: 'Experiência de retrato TheArtisan.ai',
  },
  CustomerPortraitsDownloadCode: {
    error: 'Ups! Não foi possível carregar este retrato.',
    placeholder: 'A carregar o seu retrato…',
    imageAlt: 'Retrato',
    buttonDownload: 'Transferir',
    buttonSendOrShare: 'Enviar ou Partilhar',
    buttonRetry: 'Tentar Novamente',
    shareTitle: 'O seu retrato de {clientName}',
    shareText: 'Oferecido por TheArtisan.ai',
    states: {
      processing:
        'Os seus retratos ainda estão a ser preparados. Tente novamente dentro de instantes.',
      partial:
        'Não foi possível carregar alguns retratos ou detalhes da página. As transferências disponíveis continuam a funcionar.',
    },
    actions: {
      enterAnotherCode: 'Introduzir outro código',
      contactSupport: 'Contactar o suporte',
      retryDownload: 'Tentar transferir novamente',
      downloadFailed:
        'A transferência falhou. Verifique a ligação e tente novamente.',
      linkCopied: 'Ligação copiada. Pode colá-la numa mensagem.',
      shareFailed:
        'A partilha não está disponível neste momento. Tente novamente.',
    },
  },
  CustomerDigitalExtras: {
    downloadTitle: 'As suas fotos e vídeo',
    downloadIntro: 'Guarde as suas fotos e o vídeo ou abra o seu bilhete QR.',
    photosTitle: 'As suas fotos',
    photosIntro: 'Guarde as suas fotos ou abra o seu passe QR.',
    navigation: 'As suas transferências',
    tabs: {
      photos: 'Fotos',
      extras: 'Vídeo',
      pass: 'Bilhete QR',
    },
    title: 'Os seus extras digitais',
    loading: 'A carregar os seus extras digitais…',
    loadError:
      'Não foi possível carregar os seus ficheiros. Verifique a ligação e tente novamente.',
    included: 'Incluído',
    includedOrder:
      'O seu vídeo está incluído. Guarde o bilhete QR para o abrir mais tarde.',
    preparingHelp:
      'Guarde esta ligação ou o bilhete QR. Pode fechar esta página e voltar mais tarde.',
    reference: 'Referência: {reference}',
    retry: 'Tentar novamente',
    empty: 'Não foram adicionados extras digitais a esta visita.',
    orderTitle: 'Pedido de vídeo',
    showCashier: 'Mostre este pedido e o bilhete QR na caixa.',
    videoTotal: 'Total do vídeo',
    payAtDesk: 'O pagamento é feito na caixa.',
    preparing: 'A preparar os seus ficheiros…',
    failed:
      'Não foi possível criar o seu vídeo. Mostre a referência abaixo à equipa no local para obter ajuda.',
    ready: 'Pronto para transferir',
    video: 'Foto animada',
    videoDescription:
      'O seu momento preferido em movimento. Para partilhar nas redes sociais e no grupo da família.',
    download: 'Transferir',
    share: 'Partilhar ficheiro',
    preparingShare: 'A preparar o ficheiro…',
    shareFailed:
      'A partilha não está disponível. Use «Transferir» para guardar este ficheiro.',
  },
  GoPhotoUpload: {
    waitingForPortraits: 'A finalizar os seus retratos',
    waitingForPortraitsSubtitle:
      'Aguarde enquanto os retratos restantes são criados.',
    success: 'Fotos enviadas!',
    successSubtitle: 'As suas fotos já estão disponíveis no seu PhotoPass.',
  },
  system: {
    reconnecting: 'A reconectar ao servidor…',
  },
} satisfies LocaleMessageSchema;
